import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ChevronLeft, RotateCcw, Bell, BellOff, AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  loadTimeslotConfig,
  saveTimeslotConfig,
  resetTimeslotConfig,
  getReminderTimes,
  type TimeSlotConfig,
} from "@/hooks/usePractice";
import { useNotifications } from "@/hooks/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { syncAllSettings } from "@/lib/settingsSync";
import { cn } from "@/lib/utils";

// 15-min start options (00:00–23:45)
const START_OPTIONS: { label: string; value: number }[] = [];
for (let m = 0; m < 24 * 60; m += 15) {
  const h = Math.floor(m / 60);
  const min = m % 60;
  START_OPTIONS.push({
    label: `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`,
    value: m,
  });
}

function fmtMinutes(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60) % 24;
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function TimeslotSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { status: notifStatus, updateTimes, subscribe, unsubscribe, isLoading: notifLoading } = useNotifications();
  const [slots, setSlots] = useState<TimeSlotConfig[]>(loadTimeslotConfig);

  // Re-read when Supabase settings are loaded
  useEffect(() => {
    function onSettingsLoaded() {
      setSlots(loadTimeslotConfig());
    }
    window.addEventListener("vajra-settings-loaded", onSettingsLoaded);
    return () => window.removeEventListener("vajra-settings-loaded", onSettingsLoaded);
  }, []);

  // Derived: active slots sorted by start for summary
  const activeSlots = slots
    .filter((s) => s.active)
    .sort((a, b) => (a.startHour * 60 + a.startMinute) - (b.startHour * 60 + b.startMinute));

  const reminderTimes = getReminderTimes(slots);

  // Summary: time ranges between consecutive active slots
  const timeRanges: string[] = activeSlots.map((slot, idx) => {
    const next = activeSlots[(idx + 1) % activeSlots.length];
    return `${fmtMinutes(slot.startHour * 60 + slot.startMinute)} – ${fmtMinutes(next.startHour * 60 + next.startMinute)}`;
  });

  const syncNotifications = useCallback((newSlots: TimeSlotConfig[]) => {
    if (notifStatus === "subscribed") {
      updateTimes(getReminderTimes(newSlots));
    }
  }, [notifStatus, updateTimes]);

  function saveAndSync(updated: TimeSlotConfig[]) {
    saveTimeslotConfig(updated);
    const reloaded = loadTimeslotConfig();
    syncNotifications(reloaded);
    setSlots(reloaded);
    if (user?.id) syncAllSettings(user.id);
  }

  function updateStart(index: number, totalMinutes: number) {
    saveAndSync(slots.map((s, i) =>
      i === index
        ? { ...s, startHour: Math.floor(totalMinutes / 60), startMinute: totalMinutes % 60 }
        : s
    ));
  }

  function toggleActive(index: number) {
    saveAndSync(slots.map((s, i) => i === index ? { ...s, active: !s.active } : s));
  }

  function toggleReminder(index: number) {
    saveAndSync(slots.map((s, i) => i === index ? { ...s, reminder: !s.reminder } : s));
  }

  function handleReset() {
    resetTimeslotConfig();
    const reloaded = loadTimeslotConfig();
    syncNotifications(reloaded);
    setSlots(reloaded);
    if (user?.id) syncAllSettings(user.id);
  }

  return (
    <div className="px-4 py-6 space-y-5">
      {/* Back nav */}
      <button
        type="button"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1 text-sm text-vajra-cyan hover:text-vajra-cyan/80 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Einstellungen
      </button>

      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Clock className="h-6 w-6 text-vajra-cyan" />
          <h2 className="text-2xl font-bold tracking-tight">Zeiten</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Wann möchtest du innehalten, wann möchtest du erinnert werden?
        </p>
      </div>

      {/* Slot list */}
      <Card className="border-gray-200 dark:border-[#1E3A5F]">
        <CardContent className="py-2 px-4 divide-y divide-gray-100 dark:divide-[#1E3A5F]/50">
          {slots.map((ts, idx) => {
            const startVal = ts.startHour * 60 + ts.startMinute;
            const dimmed = !ts.active && !ts.reminder;

            return (
              <div key={ts.slot} className={cn("py-3 flex items-center gap-3", dimmed && "opacity-40")}>
                {/* Emoji */}
                <div className="w-9 h-9 rounded-full bg-[#1E3A5F]/20 dark:bg-[#1E3A5F]/40 flex items-center justify-center text-base flex-shrink-0">
                  {ts.emoji}
                </div>

                {/* Name + start time select */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-500 dark:text-muted-foreground mb-1">
                    {ts.labelShort}
                  </p>
                  <select
                    value={startVal}
                    onChange={(e) => updateStart(idx, Number(e.target.value))}
                    className="border border-gray-200 dark:border-[#1E3A5F] rounded-lg text-sm font-semibold py-1.5 px-2 bg-white dark:bg-[#0f1923] text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-vajra-cyan/50 min-w-[70px]"
                  >
                    {START_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Toggles column */}
                <div className="flex flex-col items-end gap-2.5 flex-shrink-0">
                  {/* Aktiv toggle */}
                  <button
                    type="button"
                    onClick={() => toggleActive(idx)}
                    className="flex items-center gap-1.5"
                    aria-label={ts.active ? "Deaktivieren" : "Aktivieren"}
                  >
                    <span className="text-xs text-gray-400 dark:text-muted-foreground">Aktiv</span>
                    <div
                      className={cn(
                        "relative w-9 h-5 rounded-full transition-colors duration-200",
                        ts.active ? "bg-vajra-cyan" : "bg-gray-300 dark:bg-[#1E3A5F]"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200",
                          ts.active ? "left-[18px]" : "left-0.5"
                        )}
                      />
                    </div>
                  </button>

                  {/* Erinnerung toggle (bell icon) */}
                  <button
                    type="button"
                    onClick={() => toggleReminder(idx)}
                    className="flex items-center gap-1.5"
                    aria-label={ts.reminder ? "Erinnerung deaktivieren" : "Erinnerung aktivieren"}
                  >
                    <span className="text-xs text-gray-400 dark:text-muted-foreground">Erinnerung</span>
                    <div
                      className={cn(
                        "w-7 h-7 rounded-lg flex items-center justify-center transition-colors",
                        ts.reminder
                          ? "bg-[#1E3A5F]/50 text-vajra-cyan"
                          : "text-gray-300 dark:text-[#2a4060]"
                      )}
                    >
                      {ts.reminder ? (
                        <Bell className="h-3.5 w-3.5" />
                      ) : (
                        <BellOff className="h-3.5 w-3.5" />
                      )}
                    </div>
                  </button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Live summary */}
      {(activeSlots.length > 0 || reminderTimes.length > 0) && (
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4 px-4 space-y-3">
            {activeSlots.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-muted-foreground">
                  Aktive Zeiträume
                </p>
                <div className="flex flex-wrap gap-2">
                  {timeRanges.map((r, i) => (
                    <span
                      key={i}
                      className="text-xs font-medium px-2.5 py-1 rounded-full bg-vajra-cyan/10 border border-vajra-cyan/20 text-vajra-cyan"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {activeSlots.length > 0 && reminderTimes.length > 0 && (
              <div className="border-t border-gray-100 dark:border-[#1E3A5F]" />
            )}

            {reminderTimes.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-muted-foreground">
                  Erinnerungen
                </p>
                <div className="flex flex-wrap gap-2">
                  {reminderTimes.map((t) => (
                    <span
                      key={t}
                      className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Reset */}
      <button
        type="button"
        onClick={handleReset}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-200 dark:border-[#1E3A5F] text-sm text-gray-500 dark:text-muted-foreground hover:border-vajra-blue/40 hover:text-vajra-blue dark:hover:text-vajra-cyan transition-colors"
      >
        <RotateCcw className="h-4 w-4" />
        Standardzeiten wiederherstellen
      </button>

      {/* Push-Benachrichtigungen */}
      {notifStatus !== "loading" && notifStatus !== "unsupported" && (
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-4 flex items-center gap-3">
            <div className={cn(
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
              notifStatus === "subscribed" ? "bg-emerald-500/10" : "bg-gray-100 dark:bg-[#1E3A5F]/50"
            )}>
              {notifStatus === "subscribed"
                ? <Bell className="h-4 w-4 text-emerald-500" />
                : <BellOff className="h-4 w-4 text-gray-400 dark:text-muted-foreground" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">Push-Benachrichtigungen</p>
              {notifStatus === "subscribed" && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mt-0.5">
                  <CheckCircle className="h-3 w-3" /> Aktiv
                </p>
              )}
              {notifStatus === "denied" && (
                <p className="text-xs text-red-500 flex items-center gap-1 mt-0.5">
                  <AlertCircle className="h-3 w-3" /> Blockiert — in Browser-Einstellungen erlauben
                </p>
              )}
            </div>
            {notifStatus === "subscribed" && (
              <button
                type="button"
                onClick={unsubscribe}
                disabled={notifLoading}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-500 transition-colors"
              >
                {notifLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellOff className="h-3.5 w-3.5" />}
                Deaktivieren
              </button>
            )}
            {notifStatus === "unsubscribed" && (
              <button
                type="button"
                onClick={subscribe}
                disabled={notifLoading}
                className="flex items-center gap-1.5 text-xs text-vajra-cyan hover:text-vajra-cyan/80 transition-colors"
              >
                {notifLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
                Aktivieren
              </button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
